import {Module} from '@gongxigames/framework/slot/fragments/module/Module';
import {Action} from 'com/gongxigames/math/core/game/enums/Action';
import {IBackgroundView} from '@gongxigames/framework/slot/modules/background/IBackgroundView';
import {BackgroundConstants} from '@gongxigames/framework/slot/modules/background/BackgroundConstants';
import {GenericPopupEvents, GenericPopupType} from '@gongxigames/framework/slot/modules/genericPopup';
import {FlowModuleEvents} from '@gongxigames/framework/slot/modules/flow';
import {WinPresentationEvents} from '@gongxigames/framework/slot/modules/winpresentation/WinPresentationEvents';
import {DI} from '@gongxigames/framework/slot/configs/DI';
import {SoundManager} from '@gongxigames/framework/slot/services/SoundManager';
import {LogicEvents} from '@gongxigames/framework/slot/services/logic/LogicEvents';
import {inject} from '@gongxigames/framework/core';

type SpinsWinBetData = { win: number; totalBet: number; };

export class BackgroundModule extends Module<IBackgroundView> {
    public static readonly NAME: symbol = Symbol('BackgroundModule');

    public static readonly BASIC_STATE: string = 'basic';

    @inject(DI.SoundManager)
    protected _soundManager!: SoundManager;

    protected spinsWinBetHistory: SpinsWinBetData[] = [];
    protected spinsWinBetHistoryLength = 5;
    protected baseGameMusic2 = 'maingame2';

    protected setupStates(): void {
    }

    protected setupViews(): void {
        this.setView(BackgroundConstants.backgroundViewName);
    }

    protected setupEvents(): void {
        super.setupEvents();

        this.on(GenericPopupEvents.FADE_OUT_STARTED, this.onPopupFadeOutStarted);
        this.on(FlowModuleEvents.FREESPINS_RESTORED, this.transitToFreeSpins);
        this.on(FlowModuleEvents.FEATURE_FINISHED, this.onFeatureFinished);
        this.on('General.Game.STARTED', this.onGameStarted.bind(this));
        this.on(WinPresentationEvents.INITIAL_ANIMATION_STARTED, this.onInitialAnimationStarted);
        this.on(LogicEvents.SPIN_RESPONSE, this.onSpinResponse);
    }

    protected onSpinResponse(): void {
        this.updateSpinsWinBetHistory();
    }

    protected onFeatureFinished(): void {
        this.cleanSpinsWinBetHistory();
    }

    protected updateSpinsWinBetHistory(): void {
        // reset after non spin action
        if (this.logic.previousAction !== Action.SPIN) {
            this.cleanSpinsWinBetHistory();
        }

        this.spinsWinBetHistory.push({win: this.logic.getWin(), totalBet: this.logic.getTotalBet()});

        // check spins history limit
        if (this.spinsWinBetHistory.length > this.spinsWinBetHistoryLength) {
            this.spinsWinBetHistory.shift();
        }
    }

    protected cleanSpinsWinBetHistory(): void {
        this.spinsWinBetHistory = [];
    }

    protected shouldPlayLevel2Music(): boolean {
        return this.calculateAverageWinPerBet() >= 2;
    }

    protected calculateAverageWinPerBet(): number {
        let totalWin = 0;
        let totalBet = 0;

        if (this.spinsWinBetHistory.length === this.spinsWinBetHistoryLength) {
            this.spinsWinBetHistory.forEach(data => {
                totalWin += data.win;
                totalBet += data.totalBet;
            });

            return totalWin / totalBet;
        }

        return 0;
    }

    protected onPopupFadeOutStarted(id: symbol, type: symbol): void {
        if (type === GenericPopupType.FREESPIN_INTRO) {
            this.transitToFreeSpins();
        }

        if (type === GenericPopupType.FREESPIN_OUTRO) {
            this.transitToBaseGame();
        }
    }

    protected transitToFreeSpins(): void {
        this.view.playTransition(BackgroundConstants.FREE_SPINS_VIEW);
        this.playFreeSpinsBGM();
    }

    protected transitToBaseGame(): void {
        this.view.playTransition(BackgroundConstants.BASE_VIEW);
        this.playBaseGameBGM();
    }

    protected onGameStarted(): void {
        this.logic.nextAction === Action.SPIN && this.playBaseGameBGM();
    }

    protected onInitialAnimationStarted(): void {
        const music = this._soundManager.getMusic(this.baseGameMusic2);

        if (music && !music.isPlaying) {
            this.tryToPlayBaseGameMusic2();
        }
    }

    protected tryToPlayBaseGameMusic2(volume = 1): void {
        if (this.logic.spinData?.action === Action.SPIN) {
            if (this.shouldPlayLevel2Music()) {
                const music = this._soundManager.getMusic(this.baseGameMusic2);

                this.soundPlayer.playMusic(this.baseGameMusic2, () => {
                    volume = music.volume;

                    if (music && music.isPlaying) {
                        this.soundPlayer.stopMusic();
                    }

                    this.tryToPlayBaseGameMusic2(volume);
                });

                music.volume = volume;
            } else if (!this.soundPlayer.isMusicPlaying()) {
                this.playBaseGameBGM();
            }
        }
    }

    protected playBaseGameBGM(): void {
        this.soundPlayer.playMusic('maingame');
    }

    protected playFreeSpinsBGM(): void {
        this.soundPlayer.playMusic('freespin');
    }
}
